import { useState } from "react";
import { toast } from "sonner";
import { GraduationCap, Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, useApi } from "@/lib/api";
import { EDU_ICONS, ArticleBody } from "@/pages/Education";

const EMPTY = { article_id: null, title: "", summary: "", body: "", icon: "cu", published: true };
const ICON_LABEL = { cu: "Mengenal CU", hak: "Hak & kewajiban", simpanan: "Simpanan", pinjaman: "Pinjaman", keuangan: "Keuangan", keamanan: "Keamanan" };

export default function AdminEdu() {
  const { data, reload } = useApi("/edu-admin/articles");
  const [f, setF] = useState(null);
  const [preview, setPreview] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const save = async () => {
    try {
      const body = { title: f.title, summary: f.summary, body: f.body, icon: f.icon, published: f.published };
      if (f.article_id) await api.put(`/edu-admin/articles/${f.article_id}`, body);
      else await api.post("/edu-admin/articles", body);
      toast.success("Materi disimpan");
      setF(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const remove = async (id) => {
    if (confirm !== id) { setConfirm(id); return toast.info("Tekan hapus sekali lagi untuk menghapus materi"); }
    try { await api.delete(`/edu-admin/articles/${id}`); toast.success("Materi dihapus"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="admin-edu-page">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-11 w-11 rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 text-white flex items-center justify-center"><GraduationCap className="h-5 w-5" /></span>
        <div className="mr-auto"><h1 className="text-2xl font-bold text-slate-900">Pendidikan Anggota</h1><p className="text-sm text-slate-500">Kelola materi edukasi yang tampil di aplikasi anggota.</p></div>
        <button onClick={() => { setF(EMPTY); setPreview(false); }} data-testid="edu-add" className="flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold px-4 py-2 rounded-xl"><Plus className="h-4 w-4" />Tambah Materi</button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        {data?.map((a) => {
          const [Icon, tone] = EDU_ICONS[a.icon] || EDU_ICONS.cu;
          return (
            <div key={a.article_id} className="bg-white border border-slate-200 rounded-2xl p-4 flex gap-3" data-testid={`edu-admin-${a.article_id}`}>
              <span className={`h-11 w-11 rounded-xl bg-gradient-to-br ${tone} text-white flex items-center justify-center shrink-0`}><Icon className="h-5 w-5" /></span>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-900">{a.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{a.summary}</div>
                <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${a.published ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{a.published ? "Tayang" : "Draf"}</span>
              </div>
              <button onClick={() => { setF(a); setPreview(false); }} data-testid={`edu-edit-${a.article_id}`} className="p-2 h-fit text-slate-400 hover:text-blue-700" aria-label="Ubah"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(a.article_id)} data-testid={`edu-delete-${a.article_id}`} className={`p-2 h-fit hover:text-red-600 ${confirm === a.article_id ? "text-red-600 bg-red-50 rounded-lg" : "text-slate-400"}`} aria-label="Hapus"><Trash2 className="h-4 w-4" /></button>
            </div>
          );
        })}
      </div>
      <Dialog open={!!f} onOpenChange={(o) => !o && setF(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edu-editor">
          <DialogHeader><DialogTitle>{f?.article_id ? "Ubah Materi" : "Tambah Materi"}</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-3">
              <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Judul materi" data-testid="edu-title" className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm" />
              <input value={f.summary} onChange={(e) => setF({ ...f, summary: e.target.value })} placeholder="Ringkasan singkat" data-testid="edu-summary" className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm" />
              <div className="flex flex-wrap gap-3 items-center">
                <select value={f.icon} onChange={(e) => setF({ ...f, icon: e.target.value })} data-testid="edu-icon" className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
                  {Object.entries(ICON_LABEL).map(([k, l]) => <option key={k} value={k}>Ikon: {l}</option>)}
                </select>
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="edu-published" />Tayangkan ke anggota</label>
                <button onClick={() => setPreview(!preview)} data-testid="edu-preview" className="ml-auto flex items-center gap-1 text-sm font-semibold text-slate-600">{preview ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}{preview ? "Tulis" : "Pratinjau"}</button>
              </div>
              {preview ? <div className="border border-slate-200 rounded-xl p-4 max-h-[40vh] overflow-y-auto"><ArticleBody body={f.body || ""} /></div> : (
                <textarea value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} rows={12} data-testid="edu-body"
                  placeholder={"Isi materi.\n\n## Subjudul\n- poin pertama\n- poin kedua\n\nPisahkan paragraf dengan baris kosong."} className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono" />
              )}
              <button onClick={save} disabled={f.title.trim().length < 3 || f.summary.trim().length < 3 || f.body.trim().length < 10} data-testid="edu-save"
                className="w-full bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-semibold py-2.5 rounded-xl">Simpan Materi</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
